import type { ParsedValue, ParsedClasses, EditorBreakpoint } from './types';
import { EDITOR_BREAKPOINTS } from './types';
import { getNormalizedValue, resolveAmbiguousPrefix, getAllPrefixes } from './propertyMap';

function stripBrackets(value: string): string {
  if (value.startsWith('[') && value.endsWith(']')) {
    return value.slice(1, -1);
  }
  return value;
}

export function parseValue(className: string, breakpoint: EditorBreakpoint = 'base'): ParsedValue | null {
  if (!className) {
    return null;
  }

  let workingClass = className;
  let modifier: string | undefined;

  if (workingClass.includes('/')) {
    const parts = workingClass.split('/');
    if (parts.length === 2 && parts[0] && parts[1]) {
      [workingClass, modifier] = parts;
    }
  }

  if (workingClass === '[text-decoration-line:underline_line-through]') {
    return { type: 'standard', property: 'textDecoration', value: workingClass, breakpoint, modifier };
  }

  const allPrefixes = getAllPrefixes();
  const sortedPrefixes = allPrefixes.sort((a, b) => b.length - a.length);

  for (const prefix of sortedPrefixes) {
    if (workingClass === prefix) {
      const property = resolveAmbiguousPrefix(prefix, workingClass);
      if (property) {
        return {
          type: 'standard',
          property,
          value: prefix, // Store the full class name for standalone classes
          breakpoint,
          modifier,
        };
      }
    } else if (workingClass.startsWith(`${prefix}-`)) {
      const valuePart = workingClass.slice(prefix.length + 1);

      if (valuePart) {
        const property = resolveAmbiguousPrefix(prefix, workingClass);
        if (property) {
          const isArbitrary = valuePart.startsWith('[') && valuePart.endsWith(']');

          if (isArbitrary) {
            const arbitraryValue = stripBrackets(valuePart);
            return {
              type: 'arbitrary',
              property,
              value: arbitraryValue,
              breakpoint,
              normalizedValue: arbitraryValue,
              modifier,
            };
          }

          const normalizedValue = getNormalizedValue(prefix, valuePart);

          return {
            type: 'standard',
            property,
            value: valuePart,
            breakpoint,
            normalizedValue,
            modifier,
          };
        }
      }
    }
  }

  return null;
}

export function parseClasses(classString: string): ParsedClasses {
  const result: ParsedClasses = {
    unknown: [],
  };

  const breakpointPrefixes = EDITOR_BREAKPOINTS.filter((breakpoint) => breakpoint !== 'base').sort(
    (a, b) => b.length - a.length,
  );

  const classes = classString
    .trim()
    .split(/\s+/)
    .filter((c) => c.length > 0);

  for (const fullClass of classes) {
    let breakpoint: EditorBreakpoint = 'base';
    let className = fullClass;

    for (const prefix of breakpointPrefixes) {
      const prefixWithColon = `${prefix}:`;
      if (fullClass.startsWith(prefixWithColon)) {
        breakpoint = prefix as EditorBreakpoint;
        className = fullClass.slice(prefixWithColon.length);
        break;
      }
    }

    const parsedValue = parseValue(className, breakpoint);

    if (parsedValue) {
      const { property } = parsedValue;

      if (!result[property]) {
        result[property] = {};
      }

      const propertyValue = result[property];
      if (typeof propertyValue === 'object' && !Array.isArray(propertyValue)) {
        propertyValue[breakpoint] = parsedValue;
      }
    } else if (!result.unknown.includes(fullClass)) {
      result.unknown.push(fullClass);
    }
  }

  return result;
}
